import { create } from 'zustand';
import { createDefaultProject } from '../../shared/defaultProject';
import { ProjectState } from '../../shared/types';

interface ProjectStore {
  project: ProjectState;
  past: ProjectState[];
  future: ProjectState[];
  replaceProject: (project: ProjectState, trackHistory?: boolean) => void;
  updateProject: (updater: (project: ProjectState) => ProjectState, trackHistory?: boolean) => void;
  newProject: () => void;
  undo: () => void;
  redo: () => void;
}

const cloneProject = (project: ProjectState): ProjectState => structuredClone(project);

export const useProjectStore = create<ProjectStore>((set) => ({
  project: createDefaultProject(),
  past: [],
  future: [],
  replaceProject: (project, trackHistory = true) =>
    set((state) => ({
      project: cloneProject(project),
      past: trackHistory ? [...state.past.slice(-39), cloneProject(state.project)] : state.past,
      future: trackHistory ? [] : state.future,
    })),
  updateProject: (updater, trackHistory = true) =>
    set((state) => {
      const nextProject = updater(cloneProject(state.project));
      nextProject.updatedAt = new Date().toISOString();
      return {
        project: nextProject,
        past: trackHistory ? [...state.past.slice(-39), cloneProject(state.project)] : state.past,
        future: trackHistory ? [] : state.future,
      };
    }),
  newProject: () =>
    set((state) => ({
      project: createDefaultProject(),
      past: [...state.past.slice(-39), cloneProject(state.project)],
      future: [],
    })),
  undo: () =>
    set((state) => {
      const previous = state.past.at(-1);
      if (!previous) return state;
      return {
        project: cloneProject(previous),
        past: state.past.slice(0, -1),
        future: [cloneProject(state.project), ...state.future.slice(0, 39)],
      };
    }),
  redo: () =>
    set((state) => {
      const next = state.future[0];
      if (!next) return state;
      return {
        project: cloneProject(next),
        past: [...state.past.slice(-39), cloneProject(state.project)],
        future: state.future.slice(1),
      };
    }),
}));
